import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { secretSantaService } from '@/services/secretsanta.service';
import type {
  SecretSanta,
  SecretSantaAssignment,
  CreateSecretSantaDto,
  UpdateSecretSantaDto,
  JoinSecretSantaDto,
  SecretSantaFilters,
} from '@/types/gift.types';
import { ApiError } from '@/services/api';

// Secret Santa state interface
interface SecretSantaState {
  // All groups
  groups: SecretSanta[];
  // My groups
  myGroups: SecretSanta[];
  // Current group being viewed
  currentGroup: SecretSanta | null;
  // My assignments
  myAssignments: SecretSantaAssignment[];
  // Current assignment
  currentAssignment: SecretSantaAssignment | null;
  // Meta for pagination
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
  // Filters
  filters: SecretSantaFilters;
  // Loading states
  isLoading: boolean;
  isLoadingGroup: boolean;
  isLoadingMyGroups: boolean;
  isLoadingAssignments: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isJoining: boolean;
  isDrawing: boolean;
  // Error
  error: string | null;
}

// Initial state
const initialState: SecretSantaState = {
  groups: [],
  myGroups: [],
  currentGroup: null,
  myAssignments: [],
  currentAssignment: null,
  meta: null,
  filters: {
    page: 1,
    limit: 20,
    includeCompleted: false,
    sortBy: 'eventDate',
    sortOrder: 'asc',
  },
  isLoading: false,
  isLoadingGroup: false,
  isLoadingMyGroups: false,
  isLoadingAssignments: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isJoining: false,
  isDrawing: false,
  error: null,
};

// Async thunks

// Fetch all groups
export const fetchSecretSantaGroups = createAsyncThunk<
  { groups: SecretSanta[]; meta: SecretSantaState['meta'] },
  SecretSantaFilters | undefined,
  { rejectValue: string }
>('secretSanta/fetchAll', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { secretSanta: SecretSantaState };
    const mergedFilters = { ...state.secretSanta.filters, ...filters };
    const response = await secretSantaService.getAll(mergedFilters);
    return { groups: response.groups, meta: response.meta };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Wichtel-Gruppen');
  }
});

// Fetch my groups
export const fetchMySecretSantaGroups = createAsyncThunk<
  SecretSanta[],
  void,
  { rejectValue: string }
>('secretSanta/fetchMy', async (_, { rejectWithValue }) => {
  try {
    return await secretSantaService.getMyGroups();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden meiner Gruppen');
  }
});

// Fetch single group
export const fetchSecretSantaById = createAsyncThunk<
  SecretSanta,
  string,
  { rejectValue: string }
>('secretSanta/fetchById', async (id, { rejectWithValue }) => {
  try {
    return await secretSantaService.getById(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Gruppe');
  }
});

// Create group
export const createSecretSanta = createAsyncThunk<
  SecretSanta,
  CreateSecretSantaDto,
  { rejectValue: string }
>('secretSanta/create', async (data, { rejectWithValue }) => {
  try {
    return await secretSantaService.create(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen der Gruppe');
  }
});

// Update group
export const updateSecretSanta = createAsyncThunk<
  SecretSanta,
  { id: string; data: UpdateSecretSantaDto },
  { rejectValue: string }
>('secretSanta/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await secretSantaService.update(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren der Gruppe');
  }
});

// Delete group
export const deleteSecretSanta = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('secretSanta/delete', async (id, { rejectWithValue }) => {
  try {
    await secretSantaService.delete(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Loeschen der Gruppe');
  }
});

// Join group
export const joinSecretSanta = createAsyncThunk<
  { groupId: string; participant: unknown },
  { id: string; data?: JoinSecretSantaDto },
  { rejectValue: string }
>('secretSanta/join', async ({ id, data }, { rejectWithValue }) => {
  try {
    const participant = await secretSantaService.join(id, data);
    return { groupId: id, participant };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Beitreten');
  }
});

// Leave group
export const leaveSecretSanta = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('secretSanta/leave', async (id, { rejectWithValue }) => {
  try {
    await secretSantaService.leave(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Verlassen der Gruppe');
  }
});

// Draw names
export const drawSecretSanta = createAsyncThunk<
  SecretSanta,
  string,
  { rejectValue: string }
>('secretSanta/draw', async (id, { rejectWithValue }) => {
  try {
    return await secretSantaService.draw(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Auslosen');
  }
});

// Fetch my assignment for a group
export const fetchMyAssignment = createAsyncThunk<
  SecretSantaAssignment,
  string,
  { rejectValue: string }
>('secretSanta/fetchAssignment', async (id, { rejectWithValue }) => {
  try {
    return await secretSantaService.getMyAssignment(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Zuweisung');
  }
});

// Fetch all my assignments
export const fetchAllMyAssignments = createAsyncThunk<
  SecretSantaAssignment[],
  void,
  { rejectValue: string }
>('secretSanta/fetchAllAssignments', async (_, { rejectWithValue }) => {
  try {
    return await secretSantaService.getAllMyAssignments();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Zuweisungen');
  }
});

// Secret Santa slice
const secretSantaSlice = createSlice({
  name: 'secretSanta',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentGroup: (state) => {
      state.currentGroup = null;
      state.currentAssignment = null;
    },
    setFilters: (state, action: PayloadAction<Partial<SecretSantaFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 20,
        includeCompleted: false,
        sortBy: 'eventDate',
        sortOrder: 'asc',
      };
    },
    resetSecretSanta: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch all groups
    builder
      .addCase(fetchSecretSantaGroups.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSecretSantaGroups.fulfilled, (state, action) => {
        state.isLoading = false;
        state.groups = action.payload.groups;
        state.meta = action.payload.meta;
      })
      .addCase(fetchSecretSantaGroups.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch my groups
    builder
      .addCase(fetchMySecretSantaGroups.pending, (state) => {
        state.isLoadingMyGroups = true;
        state.error = null;
      })
      .addCase(fetchMySecretSantaGroups.fulfilled, (state, action) => {
        state.isLoadingMyGroups = false;
        state.myGroups = action.payload;
      })
      .addCase(fetchMySecretSantaGroups.rejected, (state, action) => {
        state.isLoadingMyGroups = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch single group
    builder
      .addCase(fetchSecretSantaById.pending, (state) => {
        state.isLoadingGroup = true;
        state.error = null;
      })
      .addCase(fetchSecretSantaById.fulfilled, (state, action) => {
        state.isLoadingGroup = false;
        state.currentGroup = action.payload;
      })
      .addCase(fetchSecretSantaById.rejected, (state, action) => {
        state.isLoadingGroup = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Create group
    builder
      .addCase(createSecretSanta.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createSecretSanta.fulfilled, (state, action) => {
        state.isCreating = false;
        state.groups.unshift(action.payload);
        state.myGroups.unshift(action.payload);
      })
      .addCase(createSecretSanta.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? 'Fehler beim Erstellen';
      });

    // Update group
    builder
      .addCase(updateSecretSanta.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateSecretSanta.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.groups.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        const myIndex = state.myGroups.findIndex((g) => g.id === action.payload.id);
        if (myIndex !== -1) {
          state.myGroups[myIndex] = action.payload;
        }
        if (state.currentGroup?.id === action.payload.id) {
          state.currentGroup = action.payload;
        }
      })
      .addCase(updateSecretSanta.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? 'Fehler beim Aktualisieren';
      });

    // Delete group
    builder
      .addCase(deleteSecretSanta.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteSecretSanta.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.groups = state.groups.filter((g) => g.id !== action.payload);
        state.myGroups = state.myGroups.filter((g) => g.id !== action.payload);
        if (state.currentGroup?.id === action.payload) {
          state.currentGroup = null;
        }
      })
      .addCase(deleteSecretSanta.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? 'Fehler beim Loeschen';
      });

    // Join group
    builder
      .addCase(joinSecretSanta.pending, (state) => {
        state.isJoining = true;
        state.error = null;
      })
      .addCase(joinSecretSanta.fulfilled, (state, action) => {
        state.isJoining = false;
        // Increment participant count in groups
        const groupIndex = state.groups.findIndex((g) => g.id === action.payload.groupId);
        if (groupIndex !== -1) {
          state.groups[groupIndex].participantCount += 1;
        }
        if (state.currentGroup?.id === action.payload.groupId) {
          state.currentGroup.participantCount += 1;
        }
      })
      .addCase(joinSecretSanta.rejected, (state, action) => {
        state.isJoining = false;
        state.error = action.payload ?? 'Fehler beim Beitreten';
      });

    // Leave group
    builder
      .addCase(leaveSecretSanta.pending, (state) => {
        state.isJoining = true;
        state.error = null;
      })
      .addCase(leaveSecretSanta.fulfilled, (state, action) => {
        state.isJoining = false;
        // Remove from my groups
        state.myGroups = state.myGroups.filter((g) => g.id !== action.payload);
        // Decrement participant count
        const groupIndex = state.groups.findIndex((g) => g.id === action.payload);
        if (groupIndex !== -1) {
          state.groups[groupIndex].participantCount -= 1;
        }
        if (state.currentGroup?.id === action.payload) {
          state.currentGroup.participantCount -= 1;
        }
      })
      .addCase(leaveSecretSanta.rejected, (state, action) => {
        state.isJoining = false;
        state.error = action.payload ?? 'Fehler beim Verlassen';
      });

    // Draw names
    builder
      .addCase(drawSecretSanta.pending, (state) => {
        state.isDrawing = true;
        state.error = null;
      })
      .addCase(drawSecretSanta.fulfilled, (state, action) => {
        state.isDrawing = false;
        // Update the group with drawn state
        const index = state.groups.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        const myIndex = state.myGroups.findIndex((g) => g.id === action.payload.id);
        if (myIndex !== -1) {
          state.myGroups[myIndex] = action.payload;
        }
        if (state.currentGroup?.id === action.payload.id) {
          state.currentGroup = action.payload;
        }
      })
      .addCase(drawSecretSanta.rejected, (state, action) => {
        state.isDrawing = false;
        state.error = action.payload ?? 'Fehler beim Auslosen';
      });

    // Fetch assignment
    builder
      .addCase(fetchMyAssignment.pending, (state) => {
        state.isLoadingAssignments = true;
        state.error = null;
      })
      .addCase(fetchMyAssignment.fulfilled, (state, action) => {
        state.isLoadingAssignments = false;
        state.currentAssignment = action.payload;
      })
      .addCase(fetchMyAssignment.rejected, (state, action) => {
        state.isLoadingAssignments = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch all assignments
    builder
      .addCase(fetchAllMyAssignments.pending, (state) => {
        state.isLoadingAssignments = true;
        state.error = null;
      })
      .addCase(fetchAllMyAssignments.fulfilled, (state, action) => {
        state.isLoadingAssignments = false;
        state.myAssignments = action.payload;
      })
      .addCase(fetchAllMyAssignments.rejected, (state, action) => {
        state.isLoadingAssignments = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });
  },
});

// Export actions
export const {
  clearError,
  clearCurrentGroup,
  setFilters,
  resetFilters,
  resetSecretSanta,
} = secretSantaSlice.actions;

// Selectors
export const selectSecretSantaGroups = (state: { secretSanta: SecretSantaState }) => state.secretSanta.groups;
export const selectMySecretSantaGroups = (state: { secretSanta: SecretSantaState }) => state.secretSanta.myGroups;
export const selectCurrentSecretSanta = (state: { secretSanta: SecretSantaState }) => state.secretSanta.currentGroup;
export const selectMyAssignments = (state: { secretSanta: SecretSantaState }) => state.secretSanta.myAssignments;
export const selectCurrentAssignment = (state: { secretSanta: SecretSantaState }) => state.secretSanta.currentAssignment;
export const selectSecretSantaMeta = (state: { secretSanta: SecretSantaState }) => state.secretSanta.meta;
export const selectSecretSantaFilters = (state: { secretSanta: SecretSantaState }) => state.secretSanta.filters;
export const selectSecretSantaLoading = (state: { secretSanta: SecretSantaState }) => ({
  isLoading: state.secretSanta.isLoading,
  isLoadingGroup: state.secretSanta.isLoadingGroup,
  isLoadingMyGroups: state.secretSanta.isLoadingMyGroups,
  isLoadingAssignments: state.secretSanta.isLoadingAssignments,
  isCreating: state.secretSanta.isCreating,
  isUpdating: state.secretSanta.isUpdating,
  isDeleting: state.secretSanta.isDeleting,
  isJoining: state.secretSanta.isJoining,
  isDrawing: state.secretSanta.isDrawing,
});
export const selectSecretSantaError = (state: { secretSanta: SecretSantaState }) => state.secretSanta.error;

// Export reducer
export default secretSantaSlice.reducer;
